import { EVENTS } from '../core/constants';
import { pushToDataLayer } from './datalayer';
import { pushToDito } from './dito';

import type { EventName, EventContext } from '../types'

const adapters = {
  DataLayer: pushToDataLayer,
  Dito: pushToDito,
};

type AdapterKey = keyof typeof adapters;

/**
 * Dispatches a tracking event to all destinations configured for the given event.
 *
 * Looks up destinations from the EVENTS configuration, calls each destination adapter
 * in parallel (providing the event name in lowercase and the supplied context), and
 * waits for all attempts to settle. Individual adapter failures are logged with the
 * "[DT]" prefix but do not prevent other destinations from running.
 *
 * @param eventName - The name of the event to dispatch
 * @param data - The event context/payload to send to adapters
 */
export async function dispatchTrackEvent(eventName: EventName, data: EventContext) {
  const eventConfig = EVENTS[eventName] as { destinations: string[] };

  if (!eventConfig) return;

  await Promise.allSettled(
    eventConfig.destinations.map(async (dest: string) => {
      try {
        // call the adapter function based on the destination mapped in the adapters object
        // eventName is converted to lowercase to match the expected format in all adapters
        await adapters[dest as AdapterKey](eventName.toLowerCase(), data, dest);
      } catch (err) {
        console.error(`[DT] Failed to send ${eventName} to ${dest}:`, err);
      }
    })
  );
}
